import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT, GAME_VERSION } from '@/config/GameConfig';
import { SoundManager, MusicTrack } from '@/systems/SoundManager';
import { SaveManager } from '@/systems/SaveManager';
import { SlotManager } from '@/systems/SlotManager';
import { AchievementManager } from '@/systems/AchievementManager';
import { EventBus } from '@/events/EventBus';
import { TECH, drawTechRect, fillTechRect, createTechButton, addShineEffect } from '@/ui/TechUI';
import { createButton } from '@/ui/UISwitch';
import { exportFullProfileJSON, importFullProfile, loadPlayerMeta } from '@/config/GodMode';
import { getCurve, setCurve, saveCurve } from '@/systems/WaveComposer';
import { isDevMode, DEV_MODE_CHANGED } from '@/systems/DevConsole';
import { incrementEasterEggStat, setEasterEggStat } from '@/systems/EasterEggs';
import { AchievementDef } from '@/config/AchievementDefs';
import { t, getLanguage, cycleLanguage } from '@/i18n/I18n';
import { GameEvents } from '@/events/EventBus';
import { SceneCinematicWatcher } from '@/cinematic/SceneCinematicWatcher';
import { setEnemyDamageMultiplier, setDifficultyParams, DIFFICULTY_LEVELS } from '@/config/GameConfig';
import { OptionsPanel } from '@/scenes/title/OptionsPanel';
import { SaveSlotsManager } from '@/scenes/title/SaveSlotsManager';
import { StatsAchievementPanel } from '@/scenes/title/StatsAchievementPanel';
import changelogRaw from '../../CHANGELOG.md?raw';

/** Parsed changelog entry */
interface ChangelogEntry {
  version: string;
  date: string;
  sections: { title: string; items: string[] }[];
}

function parseChangelog(raw: string): ChangelogEntry[] {
  const entries: ChangelogEntry[] = [];
  let current: ChangelogEntry | null = null;
  let currentSection: { title: string; items: string[] } | null = null;

  for (const line of raw.split('\n')) {
    const versionMatch = line.match(/^## (v[\d.]+)\s*—\s*(.+)/);
    if (versionMatch) {
      if (current) entries.push(current);
      current = { version: versionMatch[1], date: versionMatch[2].trim(), sections: [] };
      currentSection = null;
      continue;
    }
    if (!current) continue;
    const sectionMatch = line.match(/^### (.+)/);
    if (sectionMatch) {
      currentSection = { title: sectionMatch[1], items: [] };
      current.sections.push(currentSection);
      continue;
    }
    if (currentSection && line.match(/^- /)) {
      const clean = line.replace(/^- /, '').replace(/\*\*/g, '');
      currentSection.items.push(clean);
    }
  }
  if (current) entries.push(current);
  return entries;
}

const CHANGELOG_ENTRIES = parseChangelog(changelogRaw);

export class TitleScene extends Phaser.Scene {
  private devButtons: Phaser.GameObjects.Container | null = null;
  private videoMuted = false;
  private cinematicWatcher: SceneCinematicWatcher | null = null;
  private changelogPanel?: Phaser.GameObjects.Container;
  private changelogOpen = false;

  // ── Extracted modules ──
  private _optionsPanel!: OptionsPanel;
  private _saveSlotsManager!: SaveSlotsManager;
  private _statsPanel!: StatsAchievementPanel;

  constructor() {
    super({ key: 'TitleScene' });
  }

  create(): void {
    // ── Instantiate modules ──
    this._optionsPanel = new OptionsPanel(this);
    this._saveSlotsManager = new SaveSlotsManager(this, (sceneKey) => {
      this.scene.start(sceneKey);
    });
    this._statsPanel = new StatsAchievementPanel(this);

    AchievementManager.init(); // ensure achievement listeners are registered
    EventBus.off(DEV_MODE_CHANGED); // cleanup from previous instance

    // Achievement notification on title screen (above everything including STATS)
    EventBus.off(GameEvents.ACHIEVEMENT_UNLOCKED, undefined, this);
    EventBus.on(GameEvents.ACHIEVEMENT_UNLOCKED, (data: { id: string; badge: string; def: AchievementDef }) => {
      // Only handle if this scene is active (not GameScene)
      if (!this.scene.isActive()) return;
      this._statsPanel.achieveQueue.push(data);
      if (!this._statsPanel.isNotifying) this._statsPanel.showAchievementNotification();
    }, this);

    // Show any achievement notifications that were unlocked during gameplay
    // (TitleScene was inactive, so the real-time event was ignored)
    // Deduplicate: skip any that the real-time listener already queued
    if (SlotManager.activeSlot >= 0) {
      const pending = AchievementManager.consumePendingNotifications();
      const alreadyQueued = new Set(this._statsPanel.achieveQueue.map(d => d.id));
      for (const data of pending) {
        if (!alreadyQueued.has(data.id)) this._statsPanel.achieveQueue.push(data);
      }
      if (this._statsPanel.achieveQueue.length > 0 && !this._statsPanel.isNotifying) {
        this.time.delayedCall(500, () => this._statsPanel.showAchievementNotification());
      }
    }

    // ── Couch Potato achievement: 5 min CONTINUOUS idle on title screen ──
    // Stat is only persisted when 300s of uninterrupted idle is reached.
    // Reset to 0 on every TitleScene entry so prior partial/stale values don't count.
    if (SlotManager.activeSlot >= 0) {
      setEasterEggStat('titleIdleSeconds', 0);
      let idleSeconds = 0;
      const resetIdle = () => { idleSeconds = 0; setEasterEggStat('titleIdleSeconds', 0); };
      this.input.on('pointerdown', resetIdle);
      this.input.keyboard?.on('keydown', resetIdle);
      this.time.addEvent({
        delay: 1000,
        loop: true,
        callback: () => {
          idleSeconds++;
          setEasterEggStat('titleIdleSeconds', idleSeconds);
          if (idleSeconds >= 300) {
            AchievementManager.checkAll();
          }
        },
      });
    }

    // ── Load saved settings ──
    const savedSettings = SaveManager.getSettings();
    SoundManager.fxVolume = savedSettings.sfxVolume;
    SoundManager.musicVolume = savedSettings.musicVolume;
    SoundManager.muted = savedSettings.muted;
    // Restore full difficulty params from saved damage multiplier
    const savedLevel = DIFFICULTY_LEVELS.find(d => d.mult === savedSettings.difficultyMult);
    if (savedLevel) {
      setDifficultyParams(savedLevel);
    } else {
      setEnemyDamageMultiplier(savedSettings.difficultyMult);
    }

    // ── Background: video with easter egg rotation ──
    this.setupTitleVideo();

    SoundManager.playMusic(MusicTrack.MAIN_MENU);

    // ── Menu layout: CAMPAIGN hero button + 2x2 grid below ──
    const cx = CANVAS_WIDTH / 2;
    const heroY = CANVAS_HEIGHT - 270;
    const gridY = heroY + 55;       // first row of 2x2
    const gridGap = 10;             // gap between grid buttons
    const smallW = 115;             // half-width button for the grid

    // CAMPAIGN (left) + STATS (right) -- top row
    const heroW = 170, heroH = 38;
    const statsW = 60;
    const topRowGap = 8;
    const totalTopW = heroW + topRowGap + statsW;
    const campaignX = cx - totalTopW / 2 + heroW / 2;
    const statsX = cx + totalTopW / 2 - statsW / 2;

    const startBtn = createButton(this, campaignX, heroY, t('title.campaign'), {
      onClick: () => {
        SoundManager.play('clickConfirm');
        if (SlotManager.activeSlot < 0) {
          // No save exists -- auto-create slot 0 and go straight to campaign
          this._saveSlotsManager.autoCreateAndStart('LevelTreeScene');
        } else {
          this.scene.start('LevelTreeScene');
        }
      },
      width: heroW, height: heroH, fontSize: '16px',
    });
    const shineInterval = 4000;
    addShineEffect(this, startBtn, heroW, heroH, shineInterval);

    const hasSave = SlotManager.activeSlot >= 0;
    const statsBtn = createButton(this, statsX, heroY, t('title.stats'), {
      onClick: () => { if (!hasSave) return; SoundManager.play('click'); this.time.delayedCall(0, () => this._statsPanel.open()); },
      width: statsW, height: heroH, fontSize: '13px', color: hasSave ? 0xffaa44 : 0x555555,
    });
    if (hasSave) addShineEffect(this, statsBtn, statsW, heroH, shineInterval);
    if (!hasSave) statsBtn.setAlpha(0.4);

    // 2x2 grid: left column x, right column x
    const leftX = cx - smallW / 2 - gridGap / 2;
    const rightX = cx + smallW / 2 + gridGap / 2;
    const row2Y = gridY + 42 + gridGap;
    const gridH = 34;

    const qpBtn = createButton(this, leftX, gridY, t('title.challenge'), {
      onClick: () => {
        SoundManager.play('clickConfirm');
        if (SlotManager.activeSlot < 0) {
          this._saveSlotsManager.autoCreateAndStart('MapSelectScene');
        } else {
          this.scene.start('MapSelectScene');
        }
      },
      width: smallW, height: gridH, fontSize: '13px',
    });
    addShineEffect(this, qpBtn, smallW, gridH, shineInterval);

    const loadBtn = createButton(this, rightX, gridY, t('title.load'), {
      onClick: () => { SoundManager.play('click'); this.time.delayedCall(0, () => this._saveSlotsManager.open()); },
      width: smallW, height: gridH, fontSize: '13px',
    });
    addShineEffect(this, loadBtn, smallW, gridH, shineInterval);

    const editorBtn = createButton(this, leftX, row2Y, t('title.mapEditor'), {
      onClick: () => { SoundManager.play('clickConfirm'); this.scene.start('MapEditorScene'); },
      width: smallW, height: gridH, fontSize: '13px',
    });
    addShineEffect(this, editorBtn, smallW, gridH, shineInterval);

    const optBtn = createButton(this, rightX, row2Y, t('title.options'), {
      onClick: () => {
        SoundManager.play('click');
        this.time.delayedCall(0, () => {
          this._optionsPanel.open();
        });
      },
      width: smallW, height: gridH, fontSize: '13px',
    });
    addShineEffect(this, optBtn, smallW, gridH, shineInterval);

    // Subtle pulse on CAMPAIGN
    this.tweens.add({
      targets: startBtn,
      scaleX: 1.03, scaleY: 1.03,
      duration: 800, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });

    // ── Small reset icon button (crossed floppy disk) right of LOAD ──
    const resetSize = gridH;
    const resetX = rightX + smallW / 2 + resetSize / 2 + 4;
    const resetContainer = this.add.container(resetX, gridY);
    const resetGfx = this.add.graphics();
    // Background
    resetGfx.fillStyle(0x1a0a0a, 0.85);
    resetGfx.fillRoundedRect(-resetSize / 2, -resetSize / 2, resetSize, resetSize, 4);
    resetGfx.lineStyle(1, TECH.DANGER, 0.5);
    resetGfx.strokeRoundedRect(-resetSize / 2, -resetSize / 2, resetSize, resetSize, 4);
    resetContainer.add(resetGfx);
    // Floppy disk icon with red cross-out line
    const floppyIcon = this.add.text(0, -1, '\ud83d\udcbe', { fontSize: '16px' }).setOrigin(0.5);
    resetContainer.add(floppyIcon);
    const crossGfx = this.add.graphics();
    crossGfx.lineStyle(2, 0xff3333, 0.9);
    crossGfx.lineBetween(-8, 8, 8, -8);
    resetContainer.add(crossGfx);
    // Hit area
    const resetHit = this.add.rectangle(0, 0, resetSize, resetSize, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    resetContainer.add(resetHit);
    resetHit.on('pointerdown', () => {
      SoundManager.play('click');
      this.time.delayedCall(0, () => this._saveSlotsManager.showResetConfirm());
    });
    // Tooltip on hover
    const resetTooltip = this.add.text(0, -resetSize / 2 - 6, t('title.resetTooltip'), {
      fontSize: '9px', color: '#ff6666', fontFamily: 'monospace',
    }).setOrigin(0.5, 1).setVisible(false);
    resetContainer.add(resetTooltip);
    resetHit.on('pointerover', () => { resetGfx.setAlpha(1.2); resetTooltip.setVisible(true); });
    resetHit.on('pointerout', () => { resetGfx.setAlpha(1); resetTooltip.setVisible(false); });

    // ── Discord easter egg button ──
    const discordY = row2Y + gridH + 18;
    const discordW = totalTopW;
    const discordH = 28;
    let discordPhase = 0; // 0=initial, 1=flipped, 2+=extra clicks
    const DISCORD_MESSAGES = [
      t('title.discord.1'),  // phase 1
      t('title.discord.2'),  // phase 2
      t('title.discord.3'),  // phase 3
      t('title.discord.4'),  // phase 4
      t('title.discord.5'),  // phase 5 -> achievement
    ];
    const discordBtn = createButton(this, cx, discordY, t('title.discord'), {
      onClick: () => {
        SoundManager.play('click');
        const label = (discordBtn.list as Phaser.GameObjects.GameObject[])
          .find((o): o is Phaser.GameObjects.Text => o instanceof Phaser.GameObjects.Text);
        if (!label) return;

        if (discordPhase >= DISCORD_MESSAGES.length) return; // already done

        const msg = DISCORD_MESSAGES[discordPhase];
        discordPhase++;

        // Flip animation: squash -> change text -> unsquash
        this.tweens.add({
          targets: discordBtn,
          scaleY: 0,
          duration: 150,
          ease: 'Sine.easeIn',
          onComplete: () => {
            label.setText(msg);
            label.setFontSize(msg.length > 30 ? '8px' : '9px');
            this.tweens.add({
              targets: discordBtn,
              scaleY: 1,
              duration: 300,
              ease: 'Back.easeOut',
            });

            // Phase 3 = "OK you win, here's your Discord" -> actually open it
            if (discordPhase === 3) {
              window.open('https://discord.gg/hhXBSHtH', '_blank');
            }

            // Last message -> trigger achievement
            if (discordPhase === DISCORD_MESSAGES.length) {
              incrementEasterEggStat('discordPersistence');
              AchievementManager.checkAll();
            }
          },
        });
      },
      width: discordW, height: discordH, fontSize: '11px', color: 0x5865F2,
    });

    // ── Email subscribe form (DOM overlay) ──
    this.createSubscribeForm(cx, discordY + discordH + 30 - 35);

    this._optionsPanel.createPanel();

    this._saveSlotsManager.createPanel();

    // Dev-only buttons (small, bottom-left) -- hidden unless devMode is ON
    this.devButtons = this.add.container(0, 0);
    this.devButtons.add(createTechButton(this, 40, CANVAS_HEIGHT - 20, t('title.dev'), {
      onClick: () => { SoundManager.play('click'); this.scene.start('PowerCurveScene'); },
      width: 50, height: 22, fontSize: '9px', color: 0xff88ff,
    }));
    this.devButtons.add(createTechButton(this, 120, CANVAS_HEIGHT - 20, t('title.compGod'), {
      onClick: () => { SoundManager.play('click'); this.scene.start('WorkshopGodScene'); },
      width: 70, height: 22, fontSize: '9px', color: 0xff88ff,
    }));
    this.devButtons.add(createTechButton(this, 220, CANVAS_HEIGHT - 20, t('title.weapons'), {
      onClick: () => { SoundManager.play('click'); window.open('weapon_tuner.html', '_blank'); },
      width: 70, height: 22, fontSize: '9px', color: 0xff88ff,
    }));
    this.devButtons.add(createTechButton(this, 310, CANVAS_HEIGHT - 20, t('title.god'), {
      onClick: () => { SoundManager.play('click'); this.scene.start('GodModeScene'); },
      width: 50, height: 22, fontSize: '9px', color: 0xff88ff,
    }));
    this.devButtons.add(createTechButton(this, 390, CANVAS_HEIGHT - 20, t('title.robotLab'), {
      onClick: () => { SoundManager.play('click'); this.scene.start('RobotLabScene'); },
      width: 80, height: 22, fontSize: '9px', color: 0xff6644,
    }));
    this.devButtons.add(createTechButton(this, 490, CANVAS_HEIGHT - 20, t('title.enemyScales'), {
      onClick: () => { SoundManager.play('click'); this.scene.start('EnemyTuningScene', { returnTo: 'TitleScene' }); },
      width: 90, height: 22, fontSize: '9px', color: 0xffaa44,
    }));
    this.devButtons.add(createTechButton(this, 600, CANVAS_HEIGHT - 20, t('title.balance'), {
      onClick: () => { SoundManager.play('click'); this.scene.start('BalanceChartScene', { returnTo: 'TitleScene' }); },
      width: 70, height: 22, fontSize: '9px', color: 0x44ddaa,
    }));
    this.devButtons.add(createTechButton(this, 720, CANVAS_HEIGHT - 20, t('title.exportConfig'), {
      onClick: () => {
        SoundManager.play('clickConfirm');
        const json = exportFullProfileJSON(SoundManager.exportSfxVolumes(), getCurve());
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const playerName = loadPlayerMeta().name || 'player';
        a.href = url;
        a.download = `robogame_devconfig_${playerName.replace(/\s+/g, '_')}.json`;
        a.click();
        URL.revokeObjectURL(url);
      },
      width: 120, height: 22, fontSize: '9px', color: 0x44ffaa,
    }));
    this.devButtons.add(createTechButton(this, 880, CANVAS_HEIGHT - 20, t('title.importConfig'), {
      onClick: () => {
        SoundManager.play('clickConfirm');
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = () => {
          const file = input.files?.[0];
          if (!file) return;
          const reader = new FileReader();
          reader.onload = () => {
            const profile = importFullProfile(reader.result as string);
            if (profile) {
              if (profile.sfxVolumes) SoundManager.importSfxVolumes(profile.sfxVolumes);
              if (profile.curveParams) { setCurve(profile.curveParams); saveCurve(); }
              SoundManager.play('evolve' as any);
              this.scene.restart();
            } else {
              alert(t('title.invalidConfig'));
            }
          };
          reader.readAsText(file);
        };
        input.click();
      },
      width: 120, height: 22, fontSize: '9px', color: 0xffaa44,
    }));
    this.devButtons.add(createTechButton(this, 1040, CANVAS_HEIGHT - 20, t('title.animations'), {
      onClick: () => { SoundManager.play('click'); this.scene.start('AnimationPreviewScene'); },
      width: 100, height: 22, fontSize: '9px', color: 0x44ddff,
    }));

    // Show/hide based on saved devMode setting
    this.devButtons.setVisible(isDevMode());
    // Listen for live toggling from dev console
    EventBus.on(DEV_MODE_CHANGED, (on: boolean) => {
      this.devButtons?.setVisible(on);
    });

    // Version + prototype notice (prototype text to the left of version, same line)
    const verTxt = this.add.text(CANVAS_WIDTH - 10, CANVAS_HEIGHT - 10, `v${GAME_VERSION}`, {
      fontSize: '10px', color: '#555555', fontFamily: 'monospace',
    }).setOrigin(1, 1);
    this.add.text(verTxt.x - verTxt.width - 6, CANVAS_HEIGHT - 10, t('title.prototype'), {
      fontSize: '9px', color: '#666666', fontFamily: 'monospace', fontStyle: 'italic',
    }).setOrigin(1, 1);

    // Unique visitor counter (bottom-left) — deduplicated per session
    const visitorTxt = this.add.text(10, CANVAS_HEIGHT - 10, '', {
      fontSize: '10px', color: '#555555', fontFamily: 'monospace',
    }).setOrigin(0, 1).setAlpha(0);
    if (!location.hostname.includes('localhost')) {
      const alreadyCounted = sessionStorage.getItem('rfd_visit_counted');
      const endpoint = alreadyCounted ? '/api/visitors' : '/api/visit';
      const method = alreadyCounted ? 'GET' : 'POST';
      fetch(endpoint, { method })
        .then(r => r.json())
        .then((data: { count?: number }) => {
          if (data.count == null) return;
          sessionStorage.setItem('rfd_visit_counted', '1');
          visitorTxt.setText(`${data.count.toLocaleString()} players`);
          this.tweens.add({
            targets: visitorTxt, alpha: 1, duration: 600, ease: 'Cubic.easeOut',
          });
        })
        .catch(() => { /* silently ignore if offline */ });
    }

    // Language switcher (above speaker icon, bottom-right)
    const langBtn = this.add.text(CANVAS_WIDTH - 14, CANVAS_HEIGHT - 92, getLanguage().toUpperCase(), {
      fontSize: '14px', color: '#888888', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(1, 0.5).setInteractive({ useHandCursor: true });
    langBtn.on('pointerover', () => langBtn.setColor('#ffffff'));
    langBtn.on('pointerout', () => langBtn.setColor('#888888'));
    langBtn.on('pointerdown', () => {
      SoundManager.play('click');
      cycleLanguage();
    });
    // Restart scene when language changes to rebuild all text
    const onLangChanged = () => this.scene.restart();
    EventBus.on(GameEvents.LANGUAGE_CHANGED, onLangChanged);
    this.events.once('shutdown', () => EventBus.off(GameEvents.LANGUAGE_CHANGED, onLangChanged));

    // Video mute toggle (speaker icon, bottom-right)
    const speakerIcon = this.add.image(CANVAS_WIDTH - 22, CANVAS_HEIGHT - 48, 'icon_sound')
      .setDisplaySize(84, 84).setOrigin(0.5, 0.5).setInteractive({ useHandCursor: true });

    speakerIcon.on('pointerdown', () => {
      this.videoMuted = !this.videoMuted;
      speakerIcon.setTexture(this.videoMuted ? 'icon_nosound' : 'icon_sound');
      if (this.titleVideo) {
        this.titleVideo.setMute(this.videoMuted || SoundManager.muted);
      }
    });

    // Changelog button (top-left)
    createTechButton(this, 24, 24, '\ud83d\udccb', {
      onClick: () => { SoundManager.play('click'); this.openChangelogPanel(); },
      width: 56, height: 56, fontSize: '28px', color: 0x888888,
    });

    // ESC closes open panels (confirm > slots > options > changelog)
    this.input.keyboard?.on('keydown-ESC', () => {
      if (this._saveSlotsManager.hasConfirmPanel) { this._saveSlotsManager.closeDeleteConfirm(); return; }
      if (this.changelogOpen) { this.closeChangelogPanel(); return; }
      if (this._saveSlotsManager.isOpen) { this._saveSlotsManager.close(); return; }
      if (this._optionsPanel.isOpen) { this._optionsPanel.close(); return; }
    });

    // ── PROTOTYPE stamp ──
    this.createPrototypeStamp();

    // Cinematic trigger watcher -- fires sequences targeting TitleScene
    this.cinematicWatcher = new SceneCinematicWatcher(this, 'TitleScene');
    this.cinematicWatcher.fire('titleOpen');
    this.events.once('shutdown', () => {
      this.cinematicWatcher?.destroy();
      this.cinematicWatcher = null;
    });

  }

  // ── Title Video: ping-pong (forward + reversed file) + easter eggs ──

  private titleVideo!: Phaser.GameObjects.Video;
  private videoScaled = false;
  // Ping-pong state: 'forward' -> 'reverse' -> 'forward' -> ...
  private nextDirection: 'forward' | 'reverse' = 'forward';

  private static readonly VIDEO_FWD = 'title_video_1';
  private static readonly VIDEO_REV = 'title_video_1_rev';
  private static readonly EASTER_EGGS = ['title_video_2', 'title_video_3', 'title_video_4', 'title_video_5'];
  // Chance to insert an easter egg at each ping-pong turn (15%)
  private static readonly EASTER_EGG_CHANCE = 0.15;

  private setupTitleVideo(): void {
    // Fallback to static image if no video loaded
    if (!this.cache.video.has(TitleScene.VIDEO_FWD)) {
      const bg = this.add.image(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, 'title_screen');
      bg.setScale(Math.max(CANVAS_WIDTH / bg.width, CANVAS_HEIGHT / bg.height));
      return;
    }

    this.titleVideo = this.add.video(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, TitleScene.VIDEO_FWD);
    this.titleVideo.setMute(this.videoMuted || SoundManager.muted);
    this.titleVideo.setVolume(SoundManager.fxVolume);
    this.titleVideo.setLoop(false);
    this.titleVideo.setDepth(-1);
    this.nextDirection = 'reverse'; // after first forward, next is reverse
    this.titleVideo.play();

    // Scale to cover canvas once dimensions are known
    this.titleVideo.once('play', () => {
      const vidW = this.titleVideo.width || CANVAS_WIDTH;
      const vidH = this.titleVideo.height || CANVAS_HEIGHT;
      const scale = Math.max(CANVAS_WIDTH / vidW, CANVAS_HEIGHT / vidH);
      this.titleVideo.setScale(scale);
      this.videoScaled = true;
    });

    // When any clip finishes, pick the next one
    this.titleVideo.on('complete', () => this.onVideoComplete());

    // Sync video volume when settings change (respects video-specific mute)
    EventBus.on(GameEvents.SETTINGS_CHANGED, () => {
      if (!this.titleVideo) return;
      this.titleVideo.setMute(this.videoMuted || SoundManager.muted);
      this.titleVideo.setVolume(SoundManager.fxVolume);
    });
  }

  private onVideoComplete(): void {
    // Roll for easter egg at each turn
    const eggs = TitleScene.EASTER_EGGS.filter(k => this.cache.video.has(k));
    if (eggs.length > 0 && Math.random() < TitleScene.EASTER_EGG_CHANCE) {
      const pick = eggs[Math.floor(Math.random() * eggs.length)];
      // Track that this easter egg video was seen (for achievement)
      incrementEasterEggStat(`titleVideoSeen_${pick}`);
      AchievementManager.checkAll();
      // Don't change nextDirection -- easter egg is inserted, ping-pong resumes after
      this.playVideo(pick);
      return;
    }

    // Ping-pong: alternate forward <-> reverse
    if (this.nextDirection === 'reverse') {
      this.nextDirection = 'forward';
      this.playVideo(TitleScene.VIDEO_REV);
    } else {
      this.nextDirection = 'reverse';
      this.playVideo(TitleScene.VIDEO_FWD);
    }
  }

  private playVideo(key: string): void {
    this.titleVideo.changeSource(key);
    this.titleVideo.setMute(this.videoMuted || SoundManager.muted);
    this.titleVideo.setVolume(SoundManager.fxVolume);
    this.titleVideo.play();

    // Re-apply scale if video dimensions differ
    if (this.videoScaled) {
      const vidW = this.titleVideo.width || CANVAS_WIDTH;
      const vidH = this.titleVideo.height || CANVAS_HEIGHT;
      const scale = Math.max(CANVAS_WIDTH / vidW, CANVAS_HEIGHT / vidH);
      this.titleVideo.setScale(scale);
    }
  }

  private createPrototypeStamp(): void {
    const cx = CANVAS_WIDTH - 210;
    const cy = 110;
    const stamp = this.add.container(cx, cy).setDepth(5);

    // Semi-transparent background rectangle
    const pad = 24;
    const w = 420, h = 160;
    const bg = this.add.rectangle(0, 0, w, h, 0x000000, 0.45)
      .setStrokeStyle(3, 0xff4444, 0.9);
    stamp.add(bg);

    // Main "PROTOTYPE" text
    const mainText = this.add.text(0, -42, t('stamp.prototype'), {
      fontFamily: 'monospace',
      fontSize: '38px',
      color: '#ff4444',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
    }).setOrigin(0.5);
    stamp.add(mainText);

    // "WORK IN PROGRESS" subtitle
    const subText = this.add.text(0, 0, t('stamp.wip'), {
      fontFamily: 'monospace',
      fontSize: '18px',
      color: '#ffaa44',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5);
    stamp.add(subText);

    // Bottom line
    const bottomText = this.add.text(0, 38, t('stamp.comeback'), {
      fontFamily: 'monospace',
      fontSize: '11px',
      color: '#88ccff',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);
    stamp.add(bottomText);

    // Slight rotation for "stamp" feel
    stamp.setAngle(20);

    // Start invisible + oversized, then slam down like a real rubber stamp
    stamp.setAlpha(0);
    stamp.setScale(3);

    const slamIn = () => {
      SoundManager.play('stamp');
      this.tweens.add({
        targets: stamp,
        alpha: 1,
        scaleX: 1,
        scaleY: 1,
        duration: 150,
        ease: 'Back.easeOut',
        onComplete: () => {
          this.cameras.main.shake(80, 0.006);
          // Subtle pulse after landing
          this.tweens.add({
            targets: stamp,
            alpha: { from: 0.85, to: 1 },
            duration: 1200,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
          });
        },
      });
    };

    this.time.delayedCall(2500, slamIn);

    // Click to dismiss — comes back after 10s
    stamp.setSize(w, h);
    stamp.setInteractive({ useHandCursor: true });
    stamp.on('pointerdown', () => {
      this.tweens.killTweensOf(stamp);
      this.tweens.add({
        targets: stamp,
        alpha: 0,
        scaleX: 0.5,
        scaleY: 0.5,
        duration: 200,
        ease: 'Quad.easeIn',
        onComplete: () => {
          stamp.setScale(3);
          this.time.delayedCall(10000, slamIn);
        },
      });
    });
  }

  // ── Changelog panel (scrollable, top-left button) ──

  private openChangelogPanel(): void {
    if (this.changelogOpen) { this.closeChangelogPanel(); return; }
    this.changelogOpen = true;

    const pw = 560, ph = 560;
    const panel = this.add.container(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2).setDepth(25);
    this.changelogPanel = panel;

    // Full-screen backdrop
    const backdrop = this.add.rectangle(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT, 0x000000, 0.5)
      .setInteractive();
    backdrop.on('pointerdown', () => this.closeChangelogPanel());
    panel.add(backdrop);

    // Panel background
    const bg = this.add.graphics();
    bg.fillStyle(0x0a0a1e, 0.95);
    bg.fillRoundedRect(-pw / 2, -ph / 2, pw, ph, 8);
    bg.lineStyle(1.5, TECH.COLOR, 0.4);
    bg.strokeRoundedRect(-pw / 2, -ph / 2, pw, ph, 8);
    panel.add(bg);

    // Block click-through
    const blocker = this.add.rectangle(0, 0, pw, ph, 0x000000, 0).setInteractive();
    panel.add(blocker);

    // Title
    panel.add(this.add.text(0, -ph / 2 + 20, t('title.changelog'), {
      fontSize: '18px', color: '#cccccc', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5));

    // Close X button (top-right)
    const closeX = this.add.text(pw / 2 - 16, -ph / 2 + 12, '\u2715', {
      fontSize: '16px', color: '#666666', fontFamily: 'monospace',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    closeX.on('pointerover', () => closeX.setColor('#ffffff'));
    closeX.on('pointerout', () => closeX.setColor('#666666'));
    closeX.on('pointerdown', () => this.closeChangelogPanel());
    panel.add(closeX);

    // Scrollable viewport
    const viewTop = -ph / 2 + 44;
    const viewBottom = ph / 2 - 12;
    const viewH = viewBottom - viewTop;
    const viewW = pw - 24;

    const maskShape = this.make.graphics({ x: 0, y: 0 });
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(
      CANVAS_WIDTH / 2 - viewW / 2, CANVAS_HEIGHT / 2 + viewTop,
      viewW, viewH,
    );
    const mask = maskShape.createGeometryMask();

    const content = this.add.container(0, 0);
    content.setMask(mask);
    panel.add(content);

    // Build changelog content
    let cy = viewTop + 4;
    const leftX = -viewW / 2 + 4;

    for (const entry of CHANGELOG_ENTRIES) {
      // Version header
      const vHeader = this.add.text(leftX, cy, `${entry.version}  \u2014  ${entry.date}`, {
        fontSize: '14px', color: '#ffaa44', fontFamily: 'monospace', fontStyle: 'bold',
      });
      content.add(vHeader);
      cy += 22;

      // Sections
      for (const sec of entry.sections) {
        const secTitle = this.add.text(leftX + 8, cy, sec.title, {
          fontSize: '11px', color: '#88aacc', fontFamily: 'monospace', fontStyle: 'bold',
        });
        content.add(secTitle);
        cy += 16;

        // Items
        for (const item of sec.items) {
          const itemText = this.add.text(leftX + 16, cy, `\u2022 ${item}`, {
            fontSize: '10px', color: '#aaaaaa', fontFamily: 'monospace',
            wordWrap: { width: viewW - 40 },
          });
          content.add(itemText);
          cy += itemText.height + 3;
        }
        cy += 4;
      }

      // Separator between versions
      const sep = this.add.graphics();
      sep.lineStyle(1, 0x333355, 0.5);
      sep.lineBetween(leftX, cy + 4, leftX + viewW - 8, cy + 4);
      content.add(sep);
      cy += 14;
    }

    // Scroll state
    const contentHeight = cy - viewTop;
    const maxScroll = Math.max(0, contentHeight - viewH);
    let scrollY = 0;

    blocker.on('wheel', (_p: unknown, _dx: number, dy: number, dz: number) => {
      if (maxScroll <= 0) return;
      const delta = Math.abs(dz) > Math.abs(dy) ? dz : dy;
      scrollY = Phaser.Math.Clamp(scrollY - delta * 0.5, -maxScroll, 0);
      content.y = scrollY;
    });

    // Scroll indicator (top fade hint)
    if (maxScroll > 0) {
      const hint = this.add.text(0, ph / 2 - 14, '\u25bc scroll', {
        fontSize: '9px', color: '#555555', fontFamily: 'monospace',
      }).setOrigin(0.5);
      panel.add(hint);
      // Fade out hint after first scroll
      blocker.once('wheel', () => {
        this.tweens.add({ targets: hint, alpha: 0, duration: 500 });
      });
    }

    // Fade in
    panel.setAlpha(0);
    this.tweens.add({ targets: panel, alpha: 1, duration: 200, ease: 'Sine.easeOut' });

    // Store mask for cleanup
    (panel as any)._clMask = maskShape;
  }

  private closeChangelogPanel(): void {
    this.changelogOpen = false;
    if (this.changelogPanel) {
      const maskShape = (this.changelogPanel as any)._clMask;
      if (maskShape) maskShape.destroy();
      this.changelogPanel.destroy();
      this.changelogPanel = undefined;
    }
  }

  // ── Email Subscribe Form (DOM overlay) ──
  private createSubscribeForm(cx: number, y: number): void {
    const alreadySubscribed = localStorage.getItem('rfd_subscribed') === 'true';

    const html = document.createElement('div');
    html.innerHTML = `
      <style>
        .subscribe-wrap {
          display: flex; flex-direction: column; align-items: center; gap: 6px;
          font-family: monospace; width: 480px;
        }
        .subscribe-label {
          color: #ffcc00; font-size: 10px; text-transform: uppercase; letter-spacing: 1px;
          text-align: center;
        }
        .subscribe-row {
          display: flex; gap: 4px; align-items: center; justify-content: center;
        }
        .subscribe-input {
          width: 160px; background: #0d0d1a; border: 1px solid #666688; color: #cccccc;
          font-family: monospace; font-size: 11px; padding: 5px 8px;
          outline: none; border-radius: 0; flex-shrink: 0;
        }
        .subscribe-input:focus { border-color: #ffcc00; color: #ffffff; }
        .subscribe-input::placeholder { color: #444466; }
        .subscribe-btn {
          background: #0d0d1a; border: 1px solid #ffcc00; color: #ffcc00;
          font-family: monospace; font-size: 10px; padding: 5px 10px;
          cursor: pointer; text-transform: uppercase; letter-spacing: 1px;
          transition: background 0.15s, color 0.15s; flex-shrink: 0;
        }
        .subscribe-btn:hover { background: #ffcc00; color: #0d0d1a; }
        .subscribe-btn:disabled { border-color: #444466; color: #444466; cursor: default; background: #0d0d1a; }
        .subscribe-gdpr-inline {
          display: flex; align-items: center; gap: 4px; justify-content: center;
        }
        .subscribe-checkbox {
          accent-color: #ffcc00; cursor: pointer; flex-shrink: 0;
        }
        .subscribe-gdpr-label {
          color: #888899; font-size: 8px; cursor: pointer; line-height: 1.3; white-space: nowrap;
        }
        .subscribe-gdpr-link {
          color: #ffcc00; text-decoration: underline; cursor: pointer;
        }
        .subscribe-msg {
          font-size: 9px; text-align: center; min-height: 12px;
        }
        .subscribe-msg.success { color: #44ff88; }
        .subscribe-msg.error { color: #ff4444; }
      </style>
      <div class="subscribe-wrap">
        <div class="subscribe-label">${t('subscribe.label')}</div>
        <div class="subscribe-row">
          <input class="subscribe-input" type="email" placeholder="${t('subscribe.placeholder')}"
                 ${alreadySubscribed ? 'disabled' : ''} />
          <button class="subscribe-btn" ${alreadySubscribed ? 'disabled' : ''}>
            ${alreadySubscribed ? '\u2713' : t('subscribe.button')}
          </button>
        </div>
        <div class="subscribe-gdpr-inline">
          <input type="checkbox" class="subscribe-checkbox" id="gdpr-check"
                 ${alreadySubscribed ? 'checked disabled' : ''} />
          <label class="subscribe-gdpr-label" for="gdpr-check">
            ${t('subscribe.gdpr')}
            <span class="subscribe-gdpr-link" id="gdpr-link">[?]</span>
          </label>
        </div>
        <div class="subscribe-msg"></div>
      </div>
    `;

    const input = html.querySelector('.subscribe-input') as HTMLInputElement;
    const btn = html.querySelector('.subscribe-btn') as HTMLButtonElement;
    const checkbox = html.querySelector('.subscribe-checkbox') as HTMLInputElement;
    const gdprLink = html.querySelector('#gdpr-link') as HTMLSpanElement;
    const msgEl = html.querySelector('.subscribe-msg') as HTMLDivElement;

    const showMsg = (text: string, type: 'success' | 'error') => {
      msgEl.textContent = text;
      msgEl.className = `subscribe-msg ${type}`;
      this.time.delayedCall(4000, () => { if (msgEl.textContent === text) msgEl.textContent = ''; });
    };

    // GDPR link -> open Phaser modal
    gdprLink.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.openGdprModal();
    });

    // Submit
    btn.addEventListener('click', async () => {
      if (!checkbox.checked) {
        showMsg(t('subscribe.gdprRequired'), 'error');
        return;
      }
      const email = input.value.trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showMsg(t('subscribe.error'), 'error');
        return;
      }

      btn.disabled = true;
      btn.textContent = '...';

      try {
        const res = await fetch('/api/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, gdprConsent: true }),
        });
        const data = await res.json();
        if (res.ok) {
          showMsg(t('subscribe.success'), 'success');
          btn.textContent = '\u2713';
          input.disabled = true;
          checkbox.disabled = true;
          localStorage.setItem('rfd_subscribed', 'true');
          SoundManager.play('clickConfirm');
        } else if (data.error === 'already_subscribed') {
          showMsg(t('subscribe.alreadySubscribed'), 'success');
          btn.textContent = '\u2713';
          input.disabled = true;
          localStorage.setItem('rfd_subscribed', 'true');
        } else {
          showMsg(t('subscribe.error'), 'error');
          btn.disabled = false;
          btn.textContent = t('subscribe.button');
        }
      } catch (e) {
        console.warn('[TitleScene] subscribe failed:', e);
        showMsg(t('subscribe.error'), 'error');
        btn.disabled = false;
        btn.textContent = t('subscribe.button');
      }
    });

    // Enter key submits
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') btn.click();
    });

    // Stop Phaser from capturing keyboard when typing in input
    input.addEventListener('focus', () => { if (this.input.keyboard) this.input.keyboard.enabled = false; });
    input.addEventListener('blur', () => { if (this.input.keyboard) this.input.keyboard.enabled = true; });

    this.add.dom(cx, y, html).setOrigin(0.5, 0);
  }

  /** GDPR privacy policy modal (Phaser overlay) */
  private openGdprModal(): void {
    const pw = 400, ph = 300;
    const container = this.add.container(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2).setDepth(50);

    // Dimmed backdrop
    const bg = this.add.rectangle(0, 0, CANVAS_WIDTH * 2, CANVAS_HEIGHT * 2, 0x000000, 0.7)
      .setInteractive();
    container.add(bg);

    // Panel background
    const panelGfx = this.add.graphics();
    fillTechRect(panelGfx, -pw / 2, -ph / 2, pw, ph, TECH.BG, 0.95);
    drawTechRect(panelGfx, -pw / 2, -ph / 2, pw, ph, TECH.ACCENT, 1, 1.5);
    container.add(panelGfx);

    // Title
    const title = this.add.text(0, -ph / 2 + 20, t('subscribe.gdprTitle'), {
      fontSize: '14px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(title);

    // Body text (word-wrapped)
    const body = this.add.text(0, -ph / 2 + 50, t('subscribe.gdprText'), {
      fontSize: '10px', color: '#aaaacc', fontFamily: 'monospace',
      wordWrap: { width: pw - 40 }, lineSpacing: 4,
    }).setOrigin(0.5, 0);
    container.add(body);

    // Close button
    const closeBtn = createTechButton(this, 0, ph / 2 - 30, t('subscribe.gdprClose'), {
      onClick: () => { SoundManager.play('click'); container.destroy(); },
      width: 100, height: 28, fontSize: '11px',
    });
    container.add(closeBtn);

    // Click backdrop to close
    bg.on('pointerdown', () => { SoundManager.play('click'); container.destroy(); });

    // Pop-in animation
    container.setScale(0.8);
    this.tweens.add({ targets: container, scaleX: 1, scaleY: 1, duration: 200, ease: 'Back.easeOut' });
  }

}
